/**
 * @fileoverview ConfigureStep Component - Blur Region Configuration
 *
 * This component handles the configuration step (Step 1) of the BlurMe workflow.
 * It allows users to configure blur settings for detected faces and bodies,
 * including drag-and-drop box manipulation and bulk actions.
 *
 * @description
 * Key features:
 * - Interactive blur region boxes (drag to move, resize from corners/edges)
 * - Bulk actions to apply settings to all males, females, or everyone
 * - Individual person controls for fine-grained adjustments
 * - Box scaling controls (expand/shrink/reset)
 * - Support for both mouse and touch interactions
 *
 * @requires React
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import FileThumbnails from './FileThumbnails';

/* ========================================================================
   Z-Index Configuration
   ======================================================================== */
const Z_INDEX = {
  BOX_INACTIVE: 1,
  BOX_ACTIVE: 10,
  BOX_SELECTED: 50,
  MODAL_BACKDROP: 200,
  MODAL_CONTENT: 201
};

/**
 * ConfigureStep Component
 *
 * @param {Object} props
 * @param {Array} props.files - Array of uploaded file objects with detection data
 * @param {number} props.currentFileIndex - Index of the currently selected file
 * @param {Function} props.onFileIndexChange - Callback to change selected file
 * @param {Function} props.onFilesChange - Callback to update files state
 * @param {string|null} props.bulkGender - Selected gender for bulk actions ('all'|'male'|'female'|null)
 * @param {Function} props.onBulkGenderChange - Callback to change bulk gender selection
 * @param {string} props.bulkLevel - Selected blur level for bulk actions
 * @param {Function} props.onBulkLevelChange - Callback to change bulk blur level
 * @param {string} props.bulkTarget - Selected target for bulk actions ('face'|'body'|'both')
 * @param {Function} props.onBulkTargetChange - Callback to change bulk target
 * @param {Object} props.reanalyzeUsed - Map of file indices that have used reanalysis
 * @param {Function} props.onReanalyze - Callback to trigger reanalysis
 * @param {number|null} props.remainingUploads - Number of remaining uploads for the day
 * @param {Function} props.onShowToast - Callback to display toast notifications
 * @param {boolean} props.hasApplied - Whether blur settings have been applied
 * @param {Function} props.onHasAppliedChange - Callback to update hasApplied state
 * @returns {JSX.Element}
 */
function ConfigureStep({
  files,
  currentFileIndex,
  onFileIndexChange,
  onFilesChange,
  bulkGender,
  onBulkGenderChange,
  bulkLevel,
  onBulkLevelChange,
  bulkTarget,
  onBulkTargetChange,
  reanalyzeUsed,
  onReanalyze,
  remainingUploads = null,
  onShowToast,
  hasApplied = false,
  onHasAppliedChange
}) {
  /* ========================================================================
     Local State
     ======================================================================== */

  const currentFile = files[currentFileIndex];
  const canReanalyze = !reanalyzeUsed[currentFileIndex];

  /** UI visibility state */
  const [isIndividualControlsExpanded, setIsIndividualControlsExpanded] = useState(false);
  const [isPersonBoxesSectionExpanded, setIsPersonBoxesSectionExpanded] = useState(false);
  const [isCustomBoxesSectionExpanded, setIsCustomBoxesSectionExpanded] = useState(false);
  const [showBoxes, setShowBoxes] = useState(true);
  const [showReanalyzePopup, setShowReanalyzePopup] = useState(false);

  /** Custom blur boxes - user-created boxes for manual blurring */
  const customBlurBoxes = currentFile?.customBlurBoxes || [];

  /**
   * Drag/Resize State
   * Tracks the current drag operation including start position, box type, and mode
   */
  const [dragState, setDragState] = useState(null);
  const [activeBoxKey, setActiveBoxKey] = useState(null);
  const previewRef = useRef(null);

  /* ========================================================================
     Utility Functions
     ======================================================================== */

  /**
   * Ensure boxes are visible when user interacts with controls
   * Called on any control interaction to prevent confusion
   */
  const ensureBoxesVisible = () => setShowBoxes(true);

  /* ========================================================================
     Person Settings Handlers
     ======================================================================== */

  /**
   * Toggle blur enabled/disabled for a specific person
   * @param {string} personId - The ID of the person to toggle
   */
  const togglePerson = (personId) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      const settings = f.blurSettings[personId] || { enabled: false, level: 'high', target: 'face' };
      return {
        ...f,
        blurSettings: {
          ...f.blurSettings,
          [personId]: { ...settings, enabled: !settings.enabled }
        }
      };
    }));
  };

  /**
   * Update a specific setting for a person
   * @param {string} personId - The ID of the person to update
   * @param {string} key - The setting key ('level' or 'target')
   * @param {string} value - The new value for the setting
   */
  const updatePersonSetting = (personId, key, value) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      const settings = f.blurSettings[personId] || { enabled: true, level: 'high', target: 'face' };
      return {
        ...f,
        blurSettings: {
          ...f.blurSettings,
          [personId]: { ...settings, [key]: value }
        }
      };
    }));
  };

  /* ========================================================================
     Bulk Actions
     ======================================================================== */

  /**
   * Apply blur settings to multiple persons based on gender filter
   * Also resets all boxes to their original positions
   */
  const applyBulkSettings = () => {
    if (!bulkGender || !currentFile) return;

    const maleCount = currentFile.detections?.filter(d => d.gender === 'male').length || 0;
    const femaleCount = currentFile.detections?.filter(d => d.gender === 'female').length || 0;
    const totalCount = currentFile.detections?.length || 0;

    if (bulkGender === 'all' && totalCount === 0) {
      onShowToast?.('Can\'t apply, No person detected. You may use "Add Custom Blur Box"', 'error');
      return;
    }

    if (bulkGender === 'female' && femaleCount === 0) {
      onShowToast?.('Can\'t apply, No Female detected. You may use "Add Custom Blur Box"', 'error');
      return;
    }

    if (bulkGender === 'male' && maleCount === 0) {
      onShowToast?.('Can\'t apply, No Male detected. You may use "Add Custom Blur Box"', 'error');
      return;
    }

    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;

      const newSettings = {};
      const resetDetections = f.detections.map(person => {
        const matches = bulkGender === 'all' || person.gender === bulkGender;

        if (matches) {
          newSettings[person.id] = { enabled: true, level: bulkLevel, target: bulkTarget };
        } else {
          newSettings[person.id] = { enabled: false };
        }

        const resetPerson = { ...person };
        if (person.faceBoxOriginal) {
          resetPerson.faceBox = { ...person.faceBoxOriginal };
        }
        if (person.bodyBoxOriginal) {
          resetPerson.bodyBox = { ...person.bodyBoxOriginal };
        }
        return resetPerson;
      });

      return { ...f, detections: resetDetections, blurSettings: newSettings, customBlurBoxes: [] };
    }));

    onHasAppliedChange?.(true);
    onShowToast?.('Applied, Ready to \'Continue\'', 'success');
  };

  /* ========================================================================
     Box Manipulation - Helpers
     ======================================================================== */

  /**
   * Calculate scaled box dimensions while maintaining aspect ratio
   * @param {Object} box - Current box {x, y, w, h}
   * @param {number} scaleFactor - Positive to expand, negative to shrink
   * @returns {Object} New box coordinates {x, y, w, h}
   */
  const calculateScaledBox = (box, scaleFactor) => {
    const { x, y, w, h } = box;
    const aspectRatio = w / h;
    const baseExpand = 2;

    let expandW, expandH;
    if (aspectRatio >= 0.8 && aspectRatio <= 1.2) {
      expandW = baseExpand * scaleFactor;
      expandH = baseExpand * scaleFactor;
    } else if (aspectRatio > 1.2) {
      expandW = baseExpand * scaleFactor * aspectRatio;
      expandH = baseExpand * scaleFactor;
    } else {
      expandW = baseExpand * scaleFactor;
      expandH = baseExpand * scaleFactor / aspectRatio;
    }

    const newW = Math.max(2, Math.min(100, w + expandW));
    const newH = Math.max(2, Math.min(100, h + expandH));
    const newX = Math.max(0, Math.min(100 - newW, x - expandW / 2));
    const newY = Math.max(0, Math.min(100 - newH, y - expandH / 2));

    return { x: newX, y: newY, w: newW, h: newH };
  };

  /* ========================================================================
     Box Manipulation - Individual
     ======================================================================== */

  /**
   * Reset a specific box to its original AI-detected coordinates
   * @param {string} personId - The ID of the person
   * @param {string} boxType - The type of box ('faceBox' or 'bodyBox')
   */
  const resetBox = (personId, boxType) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;

      const detections = f.detections.map(d => {
        if (d.id !== personId) return d;

        const originalKey = `${boxType}Original`;
        if (d[originalKey]) {
          return { ...d, [boxType]: { ...d[originalKey] } };
        }
        return d;
      });

      return { ...f, detections };
    }));
  };

  /**
   * Scale a specific box proportionally while maintaining aspect ratio
   * @param {string} personId - The ID of the person
   * @param {string} boxType - The type of box ('faceBox' or 'bodyBox')
   * @param {number} scaleFactor - Positive to expand, negative to shrink
   */
  const scaleBox = (personId, boxType, scaleFactor) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;

      const detections = f.detections.map(d => {
        if (d.id !== personId || !d[boxType]) return d;

        const originalKey = `${boxType}Original`;
        const original = d[originalKey] || { ...d[boxType] };

        return {
          ...d,
          [boxType]: calculateScaledBox(d[boxType], scaleFactor),
          [originalKey]: original
        };
      });

      return { ...f, detections };
    }));
  };

  /* ========================================================================
     Box Manipulation - Batch Operations
     ======================================================================== */

  /**
   * Scale all enabled boxes of a specific type
   * Only affects boxes that are currently enabled and match the target setting
   * @param {string} boxType - The type of boxes to scale ('faceBox' or 'bodyBox')
   * @param {number} scaleFactor - Positive to expand, negative to shrink
   */
  const scaleAllBoxes = (boxType, scaleFactor) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;

      const detections = f.detections.map(d => {
        const settings = f.blurSettings[d.id] || {};
        if (!settings.enabled) return d;

        const target = settings.target || 'face';
        const shouldScale = (boxType === 'faceBox' && (target === 'face' || target === 'both')) ||
                           (boxType === 'bodyBox' && (target === 'body' || target === 'both'));

        if (!shouldScale || !d[boxType]) return d;

        const originalKey = `${boxType}Original`;
        const original = d[originalKey] || { ...d[boxType] };

        return {
          ...d,
          [boxType]: calculateScaledBox(d[boxType], scaleFactor),
          [originalKey]: original
        };
      });

      return { ...f, detections };
    }));
  };

  /**
   * Reset all enabled boxes of a specific type to their original positions
   * @param {string} boxType - The type of boxes to reset ('faceBox' or 'bodyBox')
   */
  const resetAllBoxes = (boxType) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;

      const detections = f.detections.map(d => {
        const settings = f.blurSettings[d.id] || {};
        if (!settings.enabled) return d;

        const target = settings.target || 'face';
        const shouldReset = (boxType === 'faceBox' && (target === 'face' || target === 'both')) ||
                           (boxType === 'bodyBox' && (target === 'body' || target === 'both'));

        if (!shouldReset) return d;

        const originalKey = `${boxType}Original`;
        if (d[originalKey]) {
          return { ...d, [boxType]: { ...d[originalKey] } };
        }
        return d;
      });

      return { ...f, detections };
    }));
  };

  /* ========================================================================
     Custom Blur Box Management
     ======================================================================== */

  /**
   * Add a new custom blur box to the current file
   * Creates a box with offset position based on existing boxes count
   */
  const addCustomBlurBox = () => {
    const existingCustomBoxes = currentFile?.customBlurBoxes || [];
    const newBoxNumber = existingCustomBoxes.length + 1;
    const newBoxId = `BlurBox_${newBoxNumber}`;

    // First box at center/bottom, subsequent boxes 5% above each previous one
    const baseX = 42.5; // Centered horizontally (with 15% width = centers at 50%)
    const baseY = existingCustomBoxes.length === 0 ? 77.5 : Math.max(5, 77.5 - (existingCustomBoxes.length * 5));

    const newBox = {
      id: newBoxId,
      box: { x: baseX, y: baseY, w: 15, h: 15 },
      boxOriginal: { x: baseX, y: baseY, w: 15, h: 15 },
      level: 'high',
      enabled: true
    };

    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: [...(f.customBlurBoxes || []), newBox]
      };
    }));

    // Set this new box as active/selected
    setActiveBoxKey(`${newBoxId}-customBox`);
    ensureBoxesVisible();
    onHasAppliedChange?.(true);
    onShowToast?.('Custom blur box added. Drag/Resize as needed. See "Individual Controls" for other options.', 'success', true);
  };

  /**
   * Remove a custom blur box by ID
   * @param {string} boxId - The ID of the custom box to remove
   */
  const removeCustomBlurBox = (boxId) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: (f.customBlurBoxes || []).filter(b => b.id !== boxId)
      };
    }));
    onShowToast?.('Custom blur box removed.', 'info');
  };

  /**
   * Update a custom blur box's coordinates
   * @param {string} boxId - The ID of the custom box
   * @param {Object} newBox - New box coordinates {x, y, w, h}
   */
  const updateCustomBlurBox = useCallback((boxId, newBox) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: (f.customBlurBoxes || []).map(b => {
          if (b.id !== boxId) return b;
          return { ...b, box: newBox };
        })
      };
    }));
  }, [currentFileIndex, onFilesChange]);

  /**
   * Update a custom blur box's settings (level, enabled)
   * @param {string} boxId - The ID of the custom box
   * @param {string} key - The setting key ('level' or 'enabled')
   * @param {any} value - The new value
   */
  const updateCustomBlurBoxSetting = (boxId, key, value) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: (f.customBlurBoxes || []).map(b => {
          if (b.id !== boxId) return b;
          return { ...b, [key]: value };
        })
      };
    }));
  };

  /**
   * Scale a custom blur box
   * @param {string} boxId - The ID of the custom box
   * @param {number} scaleFactor - Positive to expand, negative to shrink
   */
  const scaleCustomBlurBox = (boxId, scaleFactor) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: (f.customBlurBoxes || []).map(b => {
          if (b.id !== boxId) return b;
          const { x, y, w, h } = b.box;
          const baseExpand = 2 * scaleFactor;
          const newW = Math.max(2, Math.min(100, w + baseExpand));
          const newH = Math.max(2, Math.min(100, h + baseExpand));
          const newX = Math.max(0, Math.min(100 - newW, x - baseExpand / 2));
          const newY = Math.max(0, Math.min(100 - newH, y - baseExpand / 2));
          return { ...b, box: { x: newX, y: newY, w: newW, h: newH } };
        })
      };
    }));
  };

  /**
   * Reset a custom blur box to its original position
   * @param {string} boxId - The ID of the custom box
   */
  const resetCustomBlurBox = (boxId) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: (f.customBlurBoxes || []).map(b => {
          if (b.id !== boxId || !b.boxOriginal) return b;
          return { ...b, box: { ...b.boxOriginal } };
        })
      };
    }));
  };

  /**
   * Scale all enabled custom blur boxes
   * @param {number} scaleFactor - Positive to expand, negative to shrink
   */
  const scaleAllCustomBlurBoxes = (scaleFactor) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: (f.customBlurBoxes || []).map(b => {
          if (!b.enabled) return b;
          const { x, y, w, h } = b.box;
          const baseExpand = 2 * scaleFactor;
          const newW = Math.max(2, Math.min(100, w + baseExpand));
          const newH = Math.max(2, Math.min(100, h + baseExpand));
          const newX = Math.max(0, Math.min(100 - newW, x - baseExpand / 2));
          const newY = Math.max(0, Math.min(100 - newH, y - baseExpand / 2));
          return { ...b, box: { x: newX, y: newY, w: newW, h: newH } };
        })
      };
    }));
  };

  /**
   * Reset all enabled custom blur boxes to original positions
   */
  const resetAllCustomBlurBoxes = () => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;
      return {
        ...f,
        customBlurBoxes: (f.customBlurBoxes || []).map(b => {
          if (!b.enabled || !b.boxOriginal) return b;
          return { ...b, box: { ...b.boxOriginal } };
        })
      };
    }));
  };

  /* ========================================================================
     Drag & Resize Handlers - Mouse Events
     ======================================================================== */

  /**
   * Update box coordinates during drag/resize operations
   * Preserves the original box coordinates for reset functionality
   * @param {string} personId - The ID of the person
   * @param {string} boxType - The type of box ('faceBox' or 'bodyBox')
   * @param {Object} newBox - New box coordinates {x, y, w, h}
   */
  const updateBox = useCallback((personId, boxType, newBox) => {
    onFilesChange(prev => prev.map((f, idx) => {
      if (idx !== currentFileIndex) return f;

      const detections = f.detections.map(d => {
        if (d.id !== personId || !d[boxType]) return d;

        const originalKey = `${boxType}Original`;
        const original = d[originalKey] || { ...d[boxType] };

        return {
          ...d,
          [boxType]: newBox,
          [originalKey]: original
        };
      });

      return { ...f, detections };
    }));
  }, [currentFileIndex, onFilesChange]);

  /**
   * Handle mouse down on a box to start drag or resize operation
   * @param {MouseEvent} e - The mouse event
   * @param {string} personId - The ID of the person or custom box
   * @param {string} boxType - The type of box ('faceBox', 'bodyBox', or 'customBox')
   * @param {string} mode - The operation mode ('move'|'n'|'s'|'e'|'w'|'nw'|'ne'|'sw'|'se')
   */
  const handleBoxMouseDown = (e, personId, boxType, mode) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;

    let startBox;
    if (boxType === 'customBox') {
      const customBox = customBlurBoxes.find(b => b.id === personId);
      if (!customBox) return;
      startBox = { ...customBox.box };
    } else {
      const person = currentFile.detections.find(d => d.id === personId);
      if (!person || !person[boxType]) return;
      startBox = { ...person[boxType] };
    }

    // Bring this box to front
    setActiveBoxKey(`${personId}-${boxType}`);

    setDragState({
      personId,
      boxType,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startBox,
      rect
    });
  };

  /**
   * Handle mouse move during drag/resize operation
   * Calculates new box position based on delta from start position
   * @param {MouseEvent} e - The mouse event
   */
  const handleMouseMove = useCallback((e) => {
    if (!dragState) return;

    const { personId, boxType, mode, startX, startY, startBox, rect } = dragState;

    const deltaX = ((e.clientX - startX) / rect.width) * 100;
    const deltaY = ((e.clientY - startY) / rect.height) * 100;

    let newBox = { ...startBox };

    if (mode === 'move') {
      newBox.x = Math.max(0, Math.min(100 - startBox.w, startBox.x + deltaX));
      newBox.y = Math.max(0, Math.min(100 - startBox.h, startBox.y + deltaY));
    } else {
      if (mode.includes('n')) {
        const newY = Math.max(0, startBox.y + deltaY);
        const newH = startBox.h - (newY - startBox.y);
        if (newH >= 2) {
          newBox.y = newY;
          newBox.h = newH;
        }
      }
      if (mode.includes('s')) {
        newBox.h = Math.max(2, Math.min(100 - startBox.y, startBox.h + deltaY));
      }
      if (mode.includes('w')) {
        const newX = Math.max(0, startBox.x + deltaX);
        const newW = startBox.w - (newX - startBox.x);
        if (newW >= 2) {
          newBox.x = newX;
          newBox.w = newW;
        }
      }
      if (mode.includes('e')) {
        newBox.w = Math.max(2, Math.min(100 - startBox.x, startBox.w + deltaX));
      }
    }

    // Use different update function for custom boxes
    if (boxType === 'customBox') {
      updateCustomBlurBox(personId, newBox);
    } else {
      updateBox(personId, boxType, newBox);
    }
  }, [dragState, updateBox, updateCustomBlurBox]);

  /**
   * Handle mouse up to end drag/resize operation
   */
  const handleMouseUp = useCallback(() => {
    setDragState(null);
  }, []);

  /**
   * Effect: Attach/detach global mouse event listeners during drag operations
   * Uses window-level listeners to handle mouse movement outside the box
   */
  useEffect(() => {
    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp]);

  /* ========================================================================
     Drag & Resize Handlers - Touch Events (Mobile Support)
     ======================================================================== */

  /**
   * Handle touch start on a box to start drag or resize operation (mobile)
   * @param {TouchEvent} e - The touch event
   * @param {string} personId - The ID of the person or custom box
   * @param {string} boxType - The type of box ('faceBox', 'bodyBox', or 'customBox')
   * @param {string} mode - The operation mode ('move'|'n'|'s'|'e'|'w'|'nw'|'ne'|'sw'|'se')
   */
  const handleBoxTouchStart = (e, personId, boxType, mode) => {
    if (e.touches.length !== 1) return;
    e.stopPropagation();
    const touch = e.touches[0];

    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;

    let startBox;
    if (boxType === 'customBox') {
      const customBox = customBlurBoxes.find(b => b.id === personId);
      if (!customBox) return;
      startBox = { ...customBox.box };
    } else {
      const person = currentFile.detections.find(d => d.id === personId);
      if (!person || !person[boxType]) return;
      startBox = { ...person[boxType] };
    }

    // Bring this box to front
    setActiveBoxKey(`${personId}-${boxType}`);

    setDragState({
      personId,
      boxType,
      mode,
      startX: touch.clientX,
      startY: touch.clientY,
      startBox,
      rect
    });
  };

  /**
   * Handle touch move during drag/resize operation (mobile)
   * @param {TouchEvent} e - The touch event
   */
  const handleTouchMove = useCallback((e) => {
    if (!dragState || e.touches.length !== 1) return;
    e.preventDefault();

    const touch = e.touches[0];
    const { personId, boxType, mode, startX, startY, startBox, rect } = dragState;

    const deltaX = ((touch.clientX - startX) / rect.width) * 100;
    const deltaY = ((touch.clientY - startY) / rect.height) * 100;

    let newBox = { ...startBox };

    if (mode === 'move') {
      newBox.x = Math.max(0, Math.min(100 - startBox.w, startBox.x + deltaX));
      newBox.y = Math.max(0, Math.min(100 - startBox.h, startBox.y + deltaY));
    } else {
      if (mode.includes('n')) {
        const newY = Math.max(0, startBox.y + deltaY);
        const newH = startBox.h - (newY - startBox.y);
        if (newH >= 2) { newBox.y = newY; newBox.h = newH; }
      }
      if (mode.includes('s')) {
        newBox.h = Math.max(2, Math.min(100 - startBox.y, startBox.h + deltaY));
      }
      if (mode.includes('w')) {
        const newX = Math.max(0, startBox.x + deltaX);
        const newW = startBox.w - (newX - startBox.x);
        if (newW >= 2) { newBox.x = newX; newBox.w = newW; }
      }
      if (mode.includes('e')) {
        newBox.w = Math.max(2, Math.min(100 - startBox.x, startBox.w + deltaX));
      }
    }

    // Use different update function for custom boxes
    if (boxType === 'customBox') {
      updateCustomBlurBox(personId, newBox);
    } else {
      updateBox(personId, boxType, newBox);
    }
  }, [dragState, updateBox, updateCustomBlurBox]);

  /**
   * Handle touch end to finish drag/resize operation (mobile)
   */
  const handleTouchEnd = useCallback(() => {
    setDragState(null);
  }, []);

  /**
   * Effect: Attach/detach global touch event listeners during drag operations
   * Uses passive: false to allow preventDefault on touchmove
   */
  useEffect(() => {
    if (dragState) {
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [dragState, handleTouchMove, handleTouchEnd]);

  /**
   * Effect: Prevent default touch behavior on the preview container
   * This prevents page scrolling when touching the image/boxes area
   * Uses passive: false for cross-browser compatibility
   */
  useEffect(() => {
    const previewEl = previewRef.current;
    if (!previewEl) return;

    const preventTouchDefault = (e) => {
      // Only prevent if touching on a box (has cursor-move or resize cursor class)
      const target = e.target;
      if (target.closest('.cursor-move, .cursor-nw-resize, .cursor-ne-resize, .cursor-sw-resize, .cursor-se-resize, .cursor-n-resize, .cursor-s-resize, .cursor-e-resize, .cursor-w-resize')) {
        e.preventDefault();
      }
    };

    previewEl.addEventListener('touchstart', preventTouchDefault, { passive: false });
    return () => {
      previewEl.removeEventListener('touchstart', preventTouchDefault);
    };
  }, []);

  /* ========================================================================
     Render
     ======================================================================== */

  // Early return if no file selected
  if (!currentFile) return <p className="text-center text-gray-400">No files</p>;

  // Calculate detection counts for display
  const maleCount = currentFile.detections?.filter(d => d.gender === 'male').length || 0;
  const femaleCount = currentFile.detections?.filter(d => d.gender === 'female').length || 0;
  const totalCount = currentFile.detections?.length || 0;

  return (
    <div className="max-w-6xl mx-auto">
      {/* ================================================================
          Header Section
          - Title only
          ================================================================ */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-1">Configure Blur Regions</h2>
      </div>

      {/* ================================================================
          File Tabs
          - Thumbnail navigation when multiple files uploaded
          ================================================================ */}
      <FileThumbnails
        files={files}
        currentIndex={currentFileIndex}
        onIndexChange={onFileIndexChange}
      />

      {/* ================================================================
          Main Content Grid
          - Left: Image preview with interactive boxes (3 columns)
          - Right: Controls panel (2 columns)
          ================================================================ */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* ============================================================
            Preview Panel (Left Side - 3 columns)
            - Image display with overlay boxes
            - Box manipulation controls
            ============================================================ */}
        <div className="lg:col-span-3 bg-white/5 backdrop-blur rounded-2xl p-4">
          <div className="flex flex-col gap-2 mb-3">
            {/* Header + Counts - all inside one box */}
            <div className="bg-white/5 rounded-lg px-3 py-2 border border-white/10">
              {/* Row 1: AI Detection(s) + remaining uploads - centered */}
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="font-semibold">📍 AI Detection(s)</h3>
                {remainingUploads !== null && (
                  <span className={`text-xs ${remainingUploads === 0 ? 'text-red-500' : 'text-indigo-400'}`}>
                    {remainingUploads === 0
                      ? '(0 remaining today)'
                      : `(${remainingUploads} remaining today)`
                    }
                  </span>
                )}
              </div>
              {/* Row 2: Total / Male / Female counts - centered */}
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                    <span className="text-emerald-400 font-bold text-xs">{totalCount}</span>
                  </div>
                  <span className="text-gray-400 text-xs">👥 Total</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center">
                    <span className="text-blue-400 font-bold text-xs">{maleCount}</span>
                  </div>
                  <span className="text-gray-400 text-xs">👨 Male</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-pink-500/20 border border-pink-500/50 flex items-center justify-center">
                    <span className="text-pink-400 font-bold text-xs">{femaleCount}</span>
                  </div>
                  <span className="text-gray-400 text-xs">👩 Female</span>
                </div>
              </div>
            </div>

            {/* Box Controls Bar
                - Legend for box colors (green = blur, red = skip)
                - Global face/body expand/shrink/reset buttons
                - Only visible after user has applied settings or has custom boxes */}
            {(hasApplied || customBlurBoxes.length > 0) && (() => {
              const hasAnyFace = currentFile.detections?.some(d => {
                const s = currentFile.blurSettings[d.id];
                return s?.enabled && (s.target === 'face' || s.target === 'both');
              });
              const hasAnyBody = currentFile.detections?.some(d => {
                const s = currentFile.blurSettings[d.id];
                return s?.enabled && (s.target === 'body' || s.target === 'both');
              });
              const hasAnyCustomBox = customBlurBoxes.some(b => b.enabled);

              return (
                <div className="flex flex-col gap-2 p-2 bg-white/5 rounded-lg border border-white/10">
                  {/* Row 1: Legend - centered */}
                  <div className="flex justify-center">
                    <div className="flex items-center bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 gap-2">
                      <span className="text-xs text-white font-semibold">🟢 To Blur</span>
                      <span className="text-white/30">|</span>
                      <span className="text-xs text-white font-semibold">🔴 To Skip</span>
                      <span className="text-white/30">|</span>
                      <span className="text-xs text-white font-semibold">Select, Drag or Resize</span>
                    </div>
                  </div>

                  {/* Row 2: Show/Hide + Face + Body + Blur Box controls - centered */}
                  <div className="flex justify-center">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {/* Show/Hide toggle */}
                      <button
                        onClick={() => setShowBoxes(!showBoxes)}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          showBoxes
                            ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30'
                            : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                        }`}
                      >
                        {showBoxes ? '👁 Hide' : '👁 Show'}
                      </button>

                      {/* Face controls */}
                      {hasAnyFace && (
                        <>
                          <span className="text-white/30">|</span>
                          <div className="flex items-center bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 gap-1.5">
                            <span className="text-xs text-white font-medium">Face</span>
                            <button
                              onClick={() => { ensureBoxesVisible(); scaleAllBoxes('faceBox', -1); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-blue-500/40 text-blue-200 rounded hover:bg-blue-500/60 transition-colors font-bold"
                              title="Shrink all enabled face boxes"
                            >
                              −
                            </button>
                            <button
                              onClick={() => { ensureBoxesVisible(); resetAllBoxes('faceBox'); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-amber-500/40 text-amber-200 rounded hover:bg-amber-500/60 transition-colors"
                              title="Reset all enabled face boxes"
                            >
                              ⟲
                            </button>
                            <button
                              onClick={() => { ensureBoxesVisible(); scaleAllBoxes('faceBox', 1); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-emerald-500/40 text-emerald-200 rounded hover:bg-emerald-500/60 transition-colors font-bold"
                              title="Expand all enabled face boxes"
                            >
                              +
                            </button>
                          </div>
                        </>
                      )}

                      {/* Body controls */}
                      {hasAnyBody && (
                        <>
                          <span className="text-white/30">|</span>
                          <div className="flex items-center bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 gap-1.5">
                            <span className="text-xs text-white font-medium">Body</span>
                            <button
                              onClick={() => { ensureBoxesVisible(); scaleAllBoxes('bodyBox', -1); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-blue-500/40 text-blue-200 rounded hover:bg-blue-500/60 transition-colors font-bold"
                              title="Shrink all enabled body boxes"
                            >
                              −
                            </button>
                            <button
                              onClick={() => { ensureBoxesVisible(); resetAllBoxes('bodyBox'); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-amber-500/40 text-amber-200 rounded hover:bg-amber-500/60 transition-colors"
                              title="Reset all enabled body boxes"
                            >
                              ⟲
                            </button>
                            <button
                              onClick={() => { ensureBoxesVisible(); scaleAllBoxes('bodyBox', 1); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-emerald-500/40 text-emerald-200 rounded hover:bg-emerald-500/60 transition-colors font-bold"
                              title="Expand all enabled body boxes"
                            >
                              +
                            </button>
                          </div>
                        </>
                      )}

                      {/* Custom Blur Box controls - only show when there are custom boxes */}
                      {hasAnyCustomBox && (
                        <>
                          <span className="text-white/30">|</span>
                          <div className="flex items-center bg-purple-500/20 border border-purple-500/30 rounded-lg px-2 py-1.5 gap-1.5">
                            <span className="text-xs text-purple-200 font-medium">Blur Box</span>
                            <button
                              onClick={() => { ensureBoxesVisible(); scaleAllCustomBlurBoxes(-1); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-blue-500/40 text-blue-200 rounded hover:bg-blue-500/60 transition-colors font-bold"
                              title="Shrink all custom blur boxes"
                            >
                              −
                            </button>
                            <button
                              onClick={() => { ensureBoxesVisible(); resetAllCustomBlurBoxes(); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-amber-500/40 text-amber-200 rounded hover:bg-amber-500/60 transition-colors"
                              title="Reset all custom blur boxes"
                            >
                              ⟲
                            </button>
                            <button
                              onClick={() => { ensureBoxesVisible(); scaleAllCustomBlurBoxes(1); }}
                              className="w-6 h-6 flex items-center justify-center text-sm bg-emerald-500/40 text-emerald-200 rounded hover:bg-emerald-500/60 transition-colors font-bold"
                              title="Expand all custom blur boxes"
                            >
                              +
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Reanalyze Popup Modal
              - Shown when user clicks reanalyze button
              - Coming soon feature announcement */}
          {showReanalyzePopup && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center"
              style={{ zIndex: Z_INDEX.MODAL_BACKDROP }}
              onClick={() => setShowReanalyzePopup(false)}
            >
              <div
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-sm mx-4 border border-white/10 shadow-2xl"
                style={{ zIndex: Z_INDEX.MODAL_CONTENT }}
                onClick={e => e.stopPropagation()}
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <span className="text-3xl">🔬</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Not happy with detection?</h3>
                  <p className="text-gray-400 mb-4">Advanced AI detection with improved accuracy for faces and gender is coming soon!</p>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                    <p className="text-amber-300 text-sm">🚀 Stay tuned for updates</p>
                  </div>
                  <button
                    onClick={() => setShowReanalyzePopup(false)}
                    className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors text-white"
                  >
                    Got it!
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Image Preview Container
              - Main image display
              - Interactive overlay boxes for face/body regions
              - Supports drag-to-move and resize operations */}
          <div ref={previewRef} className="relative rounded-lg overflow-visible bg-black/30 select-none">
            <img src={currentFile.preview} alt="" className="w-full" draggable={false} />

            {/* Detection Boxes Overlay */}
            {showBoxes && currentFile.detections?.map((person) => {
              const settings = currentFile.blurSettings[person.id] || {};
              const enabled = settings.enabled === true;
              const target = settings.target || 'face';

              /**
               * Render an interactive box overlay for face or body region
               * @param {Object} box - Box coordinates {x, y, w, h} in percentages
               * @param {string} boxType - Type of box ('faceBox' or 'bodyBox')
               * @param {boolean} isDashed - Whether to use dashed border style
               * @returns {JSX.Element|null}
               */
              const renderInteractiveBox = (box, boxType, isDashed = false) => {
                if (!box) return null;

                const isActive = enabled && (
                  (boxType === 'faceBox' && (target === 'face' || target === 'both')) ||
                  (boxType === 'bodyBox' && (target === 'body' || target === 'both'))
                );

                const boxKey = `${person.id}-${boxType}`;
                const isTopBox = activeBoxKey === boxKey;

                return (
                  <div
                    className={`absolute rounded ${isDashed ? 'border-2 border-dashed' : 'border-3'} ${
                      isActive
                        ? (isTopBox ? 'border-emerald-600 cursor-move' : 'border-emerald-500 cursor-move')
                        : 'border-red-500 border-dashed opacity-60'
                    }`}
                    style={{
                      left: `${box.x}%`,
                      top: `${box.y}%`,
                      width: `${box.w}%`,
                      height: `${box.h}%`,
                      borderWidth: isTopBox ? '4px' : (isDashed ? '2px' : '3px'),
                      zIndex: isTopBox ? Z_INDEX.BOX_SELECTED : (isActive ? Z_INDEX.BOX_ACTIVE : Z_INDEX.BOX_INACTIVE),
                      boxShadow: isTopBox ? '0 0 8px rgba(16, 185, 129, 0.6)' : 'none',
                      touchAction: isActive ? 'none' : 'auto'
                    }}
                    onMouseDown={isActive ? (e) => handleBoxMouseDown(e, person.id, boxType, 'move') : undefined}
                    onTouchStart={isActive ? (e) => handleBoxTouchStart(e, person.id, boxType, 'move') : undefined}
                  >
                    {/* Label for face box only */}
                    {boxType === 'faceBox' && (
                      <span className={`absolute -top-5 left-0 text-xs px-1 rounded pointer-events-none ${
                        isActive ? 'bg-emerald-500' : 'bg-red-500'
                      }`}>
                        {person.id}
                      </span>
                    )}

                    {/* Resize handles - only for active boxes */}
                    {isActive && (
                      <>
                        {/* Corner handles */}
                        <div className={`absolute -top-1.5 -left-1.5 bg-white border-2 rounded-full cursor-nw-resize ${isTopBox ? 'w-3.5 h-3.5 border-emerald-600' : 'w-2.5 h-2.5 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 'nw'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 'nw'); }}
                        />
                        <div className={`absolute -top-1.5 -right-1.5 bg-white border-2 rounded-full cursor-ne-resize ${isTopBox ? 'w-3.5 h-3.5 border-emerald-600' : 'w-2.5 h-2.5 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 'ne'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 'ne'); }}
                        />
                        <div className={`absolute -bottom-1.5 -left-1.5 bg-white border-2 rounded-full cursor-sw-resize ${isTopBox ? 'w-3.5 h-3.5 border-emerald-600' : 'w-2.5 h-2.5 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 'sw'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 'sw'); }}
                        />
                        <div className={`absolute -bottom-1.5 -right-1.5 bg-white border-2 rounded-full cursor-se-resize ${isTopBox ? 'w-3.5 h-3.5 border-emerald-600' : 'w-2.5 h-2.5 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 'se'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 'se'); }}
                        />
                        {/* Edge handles */}
                        <div className={`absolute -top-1 left-1/2 -translate-x-1/2 bg-white border-2 rounded-full cursor-n-resize ${isTopBox ? 'w-5 h-2 border-emerald-600' : 'w-4 h-1.5 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 'n'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 'n'); }}
                        />
                        <div className={`absolute -bottom-1 left-1/2 -translate-x-1/2 bg-white border-2 rounded-full cursor-s-resize ${isTopBox ? 'w-5 h-2 border-emerald-600' : 'w-4 h-1.5 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 's'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 's'); }}
                        />
                        <div className={`absolute top-1/2 -left-1 -translate-y-1/2 bg-white border-2 rounded-full cursor-w-resize ${isTopBox ? 'w-2 h-5 border-emerald-600' : 'w-1.5 h-4 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 'w'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 'w'); }}
                        />
                        <div className={`absolute top-1/2 -right-1 -translate-y-1/2 bg-white border-2 rounded-full cursor-e-resize ${isTopBox ? 'w-2 h-5 border-emerald-600' : 'w-1.5 h-4 border-emerald-500'}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, person.id, boxType, 'e'); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, person.id, boxType, 'e'); }}
                        />
                      </>
                    )}
                  </div>
                );
              };

              const showBodyBox = enabled && (target === 'body' || target === 'both');

              return (
                <React.Fragment key={person.id}>
                  {renderInteractiveBox(person.faceBox, 'faceBox', false)}
                  {showBodyBox && person.bodyBox && renderInteractiveBox(person.bodyBox, 'bodyBox', true)}
                </React.Fragment>
              );
            })}

            {/* Custom Blur Boxes */}
            {showBoxes && customBlurBoxes.map((customBox) => {
              const boxKey = `${customBox.id}-customBox`;
              const isTopBox = activeBoxKey === boxKey;
              const isActive = customBox.enabled;

              return (
                <div
                  key={customBox.id}
                  className={`absolute rounded border-2 ${
                    isActive
                      ? (isTopBox ? 'border-purple-500 cursor-move' : 'border-purple-400 cursor-move')
                      : 'border-gray-500 border-dashed opacity-60'
                  }`}
                  style={{
                    left: `${customBox.box.x}%`,
                    top: `${customBox.box.y}%`,
                    width: `${customBox.box.w}%`,
                    height: `${customBox.box.h}%`,
                    borderWidth: isTopBox ? '4px' : '2px',
                    zIndex: isTopBox ? Z_INDEX.BOX_SELECTED : (isActive ? Z_INDEX.BOX_ACTIVE : Z_INDEX.BOX_INACTIVE),
                    boxShadow: isTopBox ? '0 0 8px rgba(168, 85, 247, 0.6)' : 'none',
                    touchAction: isActive ? 'none' : 'auto'
                  }}
                  onMouseDown={isActive ? (e) => handleBoxMouseDown(e, customBox.id, 'customBox', 'move') : undefined}
                  onTouchStart={isActive ? (e) => handleBoxTouchStart(e, customBox.id, 'customBox', 'move') : undefined}
                >
                  {/* Label */}
                  <span className={`absolute -top-5 left-0 text-xs px-1 rounded pointer-events-none ${
                    isActive ? 'bg-purple-500' : 'bg-gray-500'
                  }`}>
                    {customBox.id}
                  </span>

                  {/* Resize handles - only for active boxes */}
                  {isActive && (
                    <>
                      {/* Corner handles */}
                      <div className={`absolute -top-1.5 -left-1.5 bg-white border-2 rounded-full cursor-nw-resize ${isTopBox ? 'w-3.5 h-3.5 border-purple-500' : 'w-2.5 h-2.5 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 'nw'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 'nw'); }}
                      />
                      <div className={`absolute -top-1.5 -right-1.5 bg-white border-2 rounded-full cursor-ne-resize ${isTopBox ? 'w-3.5 h-3.5 border-purple-500' : 'w-2.5 h-2.5 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 'ne'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 'ne'); }}
                      />
                      <div className={`absolute -bottom-1.5 -left-1.5 bg-white border-2 rounded-full cursor-sw-resize ${isTopBox ? 'w-3.5 h-3.5 border-purple-500' : 'w-2.5 h-2.5 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 'sw'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 'sw'); }}
                      />
                      <div className={`absolute -bottom-1.5 -right-1.5 bg-white border-2 rounded-full cursor-se-resize ${isTopBox ? 'w-3.5 h-3.5 border-purple-500' : 'w-2.5 h-2.5 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 'se'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 'se'); }}
                      />
                      {/* Edge handles */}
                      <div className={`absolute -top-1 left-1/2 -translate-x-1/2 bg-white border-2 rounded-full cursor-n-resize ${isTopBox ? 'w-5 h-2 border-purple-500' : 'w-4 h-1.5 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 'n'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 'n'); }}
                      />
                      <div className={`absolute -bottom-1 left-1/2 -translate-x-1/2 bg-white border-2 rounded-full cursor-s-resize ${isTopBox ? 'w-5 h-2 border-purple-500' : 'w-4 h-1.5 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 's'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 's'); }}
                      />
                      <div className={`absolute top-1/2 -left-1 -translate-y-1/2 bg-white border-2 rounded-full cursor-w-resize ${isTopBox ? 'w-2 h-5 border-purple-500' : 'w-1.5 h-4 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 'w'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 'w'); }}
                      />
                      <div className={`absolute top-1/2 -right-1 -translate-y-1/2 bg-white border-2 rounded-full cursor-e-resize ${isTopBox ? 'w-2 h-5 border-purple-500' : 'w-1.5 h-4 border-purple-400'}`}
                        onMouseDown={(e) => { e.stopPropagation(); handleBoxMouseDown(e, customBox.id, 'customBox', 'e'); }}
                        onTouchStart={(e) => { e.stopPropagation(); handleBoxTouchStart(e, customBox.id, 'customBox', 'e'); }}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action buttons row below image */}
          <div className="flex justify-center mt-3">
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg px-3 py-2 gap-3">
              <span className="text-xs text-gray-400">Not Happy?</span>
              <span className="text-white/20">|</span>
              <button
                onClick={() => setShowReanalyzePopup(true)}
                className="text-xs px-3 py-1 bg-amber-500/20 text-amber-300 rounded-lg hover:bg-amber-500/30 transition-colors"
              >
                🔄 ReProcess (Advanced AI)
              </button>
              <span className="text-white/20">|</span>
              <button
                onClick={addCustomBlurBox}
                className="text-xs px-3 py-1 bg-purple-500/30 text-purple-200 rounded-lg hover:bg-purple-500/50 transition-colors font-medium"
                title="Add a custom blur box"
              >
                Add Custom Blur Box
              </button>
            </div>
          </div>
        </div>

        {/* ============================================================
            Controls Panel (Right Side - 2 columns)
            - Quick bulk actions
            - Individual person controls
            ============================================================ */}
        <div className="lg:col-span-2 space-y-4">
          {/* Bulk Actions Section
              - Gender filter buttons (All/Male/Female)
              - Blur level dropdown
              - Target selection (Face/Body/Both)
              - Apply button */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-4">
            <h3 className="font-semibold mb-3">Quick Blur Actions</h3>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {['all', 'male', 'female'].map(g => (
                <button
                  key={g}
                  onClick={() => { ensureBoxesVisible(); onBulkGenderChange(bulkGender === g ? null : g); }}
                  className={`p-2 rounded-lg border transition-all ${
                    bulkGender === g
                      ? 'bg-emerald-500/30 border-emerald-500'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  {g === 'all' ? '👥' : g === 'male' ? '👨' : '👩'} {g.charAt(0).toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <select
                value={bulkLevel}
                onChange={(e) => { ensureBoxesVisible(); onBulkLevelChange(e.target.value); }}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white [&>option]:bg-gray-800 [&>option]:text-white"
              >
                <option value="low">Low Blur</option>
                <option value="high">High Blur</option>
                <option value="extreme">Extreme</option>
              </select>
              <select
                value={bulkTarget}
                onChange={(e) => { ensureBoxesVisible(); onBulkTargetChange(e.target.value); }}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white [&>option]:bg-gray-800 [&>option]:text-white"
              >
                <option value="face">Face Only</option>
                <option value="body">Body Only</option>
                <option value="both">Both</option>
              </select>
            </div>
            <button
              onClick={() => { ensureBoxesVisible(); applyBulkSettings(); }}
              disabled={!bulkGender}
              className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
            >
              Apply to {bulkGender === 'all' ? 'All' : bulkGender ? `${bulkGender}s` : '...'} (Reset)
            </button>
            {bulkGender && bulkGender !== 'all' && (
              <p className="text-xs text-amber-400 mt-2 text-center">⚡ Blur {bulkGender}s, skip {bulkGender === 'male' ? 'females' : 'males'}, resets boxes</p>
            )}
          </div>

          {/* Individual Controls Section
              - Collapsible panel for per-person settings
              - Toggle blur on/off per person
              - Per-person level and target settings
              - Per-person box manipulation controls */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-4">
            <button
              onClick={() => setIsIndividualControlsExpanded(!isIndividualControlsExpanded)}
              className="w-full flex items-center justify-between font-semibold mb-3 hover:text-emerald-400 transition-colors"
            >
              <span>Individual Controls</span>
              <svg
                className={`w-5 h-5 transition-transform ${isIndividualControlsExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isIndividualControlsExpanded && (
              <div className="max-h-80 overflow-y-auto space-y-2">
                {/* Person(s) Box(es) Section */}
                {currentFile.detections?.length > 0 && (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setIsPersonBoxesSectionExpanded(!isPersonBoxesSectionExpanded)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
                    >
                      <span className="text-sm font-medium text-emerald-300">
                        👥 Person(s) <span className="text-xs text-gray-400">({currentFile.detections.length})</span>
                      </span>
                      <svg
                        className={`w-4 h-4 text-emerald-400 transition-transform ${isPersonBoxesSectionExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isPersonBoxesSectionExpanded && (
                      <div className="p-2">
                        {currentFile.detections?.map(person => {
                          const settings = currentFile.blurSettings[person.id] || { enabled: false, level: 'high', target: 'face' };

                          return (
                            <div
                              key={person.id}
                              className={`mb-2 p-3 rounded-lg transition-colors ${
                                settings.enabled ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-white/5 border border-white/10'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">
                                  {person.gender === 'male' ? '👨' : '👩'} {person.id}
                                  <span className="text-xs text-gray-400 ml-1">({Math.round(person.confidence * 100)}%)</span>
                                </span>
                                <button
                                  onClick={() => { ensureBoxesVisible(); togglePerson(person.id); }}
                                  className={`w-11 h-6 rounded-full transition-colors ${
                                    settings.enabled ? 'bg-emerald-500' : 'bg-gray-600'
                                  }`}
                                >
                                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ml-0.5 ${
                                    settings.enabled ? 'translate-x-5' : ''
                                  }`} />
                                </button>
                              </div>

                              {settings.enabled && (
                                <>
                                  <div className="grid grid-cols-2 gap-2 mb-2">
                                    <select
                                      value={settings.level}
                                      onChange={(e) => { ensureBoxesVisible(); updatePersonSetting(person.id, 'level', e.target.value); }}
                                      className="bg-white/10 rounded px-2 py-1 text-xs text-white [&>option]:bg-gray-800 [&>option]:text-white"
                                    >
                                      <option value="low">Low</option>
                                      <option value="high">High</option>
                                      <option value="extreme">Extreme</option>
                                    </select>
                                    <select
                                      value={settings.target}
                                      onChange={(e) => { ensureBoxesVisible(); updatePersonSetting(person.id, 'target', e.target.value); }}
                                      className="bg-white/10 rounded px-2 py-1 text-xs text-white [&>option]:bg-gray-800 [&>option]:text-white"
                                    >
                                      <option value="face">Face</option>
                                      <option value="body">Body</option>
                                      <option value="both">Both</option>
                                    </select>
                                  </div>

                                  {/* Box control buttons */}
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {(settings.target === 'face' || settings.target === 'both') && person.faceBox && (
                                      <div className="flex items-center bg-white/10 border border-white/20 rounded-lg px-2 py-1 gap-1">
                                        <span className="text-xs text-white font-medium">Face</span>
                                        <button
                                          onClick={() => { ensureBoxesVisible(); scaleBox(person.id, 'faceBox', -1); }}
                                          className="w-5 h-5 flex items-center justify-center text-xs bg-blue-500/40 text-blue-200 rounded hover:bg-blue-500/60 transition-colors font-bold"
                                          title="Shrink face box"
                                        >
                                          −
                                        </button>
                                        <button
                                          onClick={() => { ensureBoxesVisible(); resetBox(person.id, 'faceBox'); }}
                                          className="w-5 h-5 flex items-center justify-center text-xs bg-amber-500/40 text-amber-200 rounded hover:bg-amber-500/60 transition-colors"
                                          title="Reset face box to original"
                                        >
                                          ⟲
                                        </button>
                                        <button
                                          onClick={() => { ensureBoxesVisible(); scaleBox(person.id, 'faceBox', 1); }}
                                          className="w-5 h-5 flex items-center justify-center text-xs bg-emerald-500/40 text-emerald-200 rounded hover:bg-emerald-500/60 transition-colors font-bold"
                                          title="Expand face box"
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                    {(settings.target === 'body' || settings.target === 'both') && person.bodyBox && (
                                      <div className="flex items-center bg-white/10 border border-white/20 rounded-lg px-2 py-1 gap-1">
                                        <span className="text-xs text-white font-medium">Body</span>
                                        <button
                                          onClick={() => { ensureBoxesVisible(); scaleBox(person.id, 'bodyBox', -1); }}
                                          className="w-5 h-5 flex items-center justify-center text-xs bg-blue-500/40 text-blue-200 rounded hover:bg-blue-500/60 transition-colors font-bold"
                                          title="Shrink body box"
                                        >
                                          −
                                        </button>
                                        <button
                                          onClick={() => { ensureBoxesVisible(); resetBox(person.id, 'bodyBox'); }}
                                          className="w-5 h-5 flex items-center justify-center text-xs bg-amber-500/40 text-amber-200 rounded hover:bg-amber-500/60 transition-colors"
                                          title="Reset body box to original"
                                        >
                                          ⟲
                                        </button>
                                        <button
                                          onClick={() => { ensureBoxesVisible(); scaleBox(person.id, 'bodyBox', 1); }}
                                          className="w-5 h-5 flex items-center justify-center text-xs bg-emerald-500/40 text-emerald-200 rounded hover:bg-emerald-500/60 transition-colors font-bold"
                                          title="Expand body box"
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Custom Blur Box(es) Section */}
                {customBlurBoxes.length > 0 && (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setIsCustomBoxesSectionExpanded(!isCustomBoxesSectionExpanded)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
                    >
                      <span className="text-sm font-medium text-purple-300">
                        📦 Custom Blur(s) <span className="text-xs text-gray-400">({customBlurBoxes.length})</span>
                      </span>
                      <svg
                        className={`w-4 h-4 text-purple-400 transition-transform ${isCustomBoxesSectionExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isCustomBoxesSectionExpanded && (
                      <div className="p-2">
                        {customBlurBoxes.map(customBox => (
                          <div
                            key={customBox.id}
                            className={`mb-2 p-3 rounded-lg transition-colors ${
                              customBox.enabled ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-white/5 border border-white/10'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">
                                📦 {customBox.id}
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => { ensureBoxesVisible(); updateCustomBlurBoxSetting(customBox.id, 'enabled', !customBox.enabled); }}
                                  className={`w-11 h-6 rounded-full transition-colors ${
                                    customBox.enabled ? 'bg-purple-500' : 'bg-gray-600'
                                  }`}
                                >
                                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ml-0.5 ${
                                    customBox.enabled ? 'translate-x-5' : ''
                                  }`} />
                                </button>
                                <button
                                  onClick={() => removeCustomBlurBox(customBox.id)}
                                  className="w-6 h-6 flex items-center justify-center text-xs bg-red-500/40 text-red-200 rounded hover:bg-red-500/60 transition-colors"
                                  title="Remove this blur box"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>

                            {customBox.enabled && (
                              <>
                                <div className="mb-2">
                                  <select
                                    value={customBox.level}
                                    onChange={(e) => { ensureBoxesVisible(); updateCustomBlurBoxSetting(customBox.id, 'level', e.target.value); }}
                                    className="w-full bg-white/10 rounded px-2 py-1 text-xs text-white [&>option]:bg-gray-800 [&>option]:text-white"
                                  >
                                    <option value="low">Low Blur</option>
                                    <option value="high">High Blur</option>
                                    <option value="extreme">Extreme</option>
                                  </select>
                                </div>

                                {/* Box control buttons */}
                                <div className="flex items-center bg-purple-500/20 border border-purple-500/30 rounded-lg px-2 py-1 gap-1">
                                  <span className="text-xs text-purple-200 font-medium">Size</span>
                                  <button
                                    onClick={() => { ensureBoxesVisible(); scaleCustomBlurBox(customBox.id, -1); }}
                                    className="w-5 h-5 flex items-center justify-center text-xs bg-blue-500/40 text-blue-200 rounded hover:bg-blue-500/60 transition-colors font-bold"
                                    title="Shrink blur box"
                                  >
                                    −
                                  </button>
                                  <button
                                    onClick={() => { ensureBoxesVisible(); resetCustomBlurBox(customBox.id); }}
                                    className="w-5 h-5 flex items-center justify-center text-xs bg-amber-500/40 text-amber-200 rounded hover:bg-amber-500/60 transition-colors"
                                    title="Reset blur box to original"
                                  >
                                    ⟲
                                  </button>
                                  <button
                                    onClick={() => { ensureBoxesVisible(); scaleCustomBlurBox(customBox.id, 1); }}
                                    className="w-5 h-5 flex items-center justify-center text-xs bg-emerald-500/40 text-emerald-200 rounded hover:bg-emerald-500/60 transition-colors font-bold"
                                    title="Expand blur box"
                                  >
                                    +
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* No detections and no custom boxes message */}
                {currentFile.detections?.length === 0 && customBlurBoxes.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">No people detected. Use "Add Blur Box" to create custom blur regions.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfigureStep;
